import { datadogRum } from '@datadog/browser-rum';

import { ddIgnorableErrors } from '~/constants/ddIgnorableErrors';

const datadogOptions = {
  applicationId: process.env.NEXT_PUBLIC_DATADOG_APP_ID || '',
  clientToken: process.env.NEXT_PUBLIC_DATADOG_TOKEN || '',
  env: process.env.NEXT_PUBLIC_DATADOG_ENV,
  version: process.env.NEXT_PUBLIC_APP_RELEASE,
  site: 'datadoghq.eu' as const,
  service: 'echo-orca-web-app',
  sessionSampleRate:
    process.env.NEXT_PUBLIC_DATADOG_ENV === 'production' ||
    process.env.NEXT_PUBLIC_DATADOG_ENV === 'local'
      ? 100
      : 50,
  sessionReplaySampleRate:
    process.env.NEXT_PUBLIC_DATADOG_ENV === 'production' ||
    process.env.NEXT_PUBLIC_DATADOG_ENV === 'local'
      ? 100
      : 0,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: 'allow' as const,
  enableExperimentalFeatures: ['feature_flags'] as ['feature_flags'],
  actionNameAttribute: 'track-id',
  allowedTracingUrls: [
    process.env.NEXT_PUBLIC_ORCA_API_RESOURCE,
    process.env.NEXT_PUBLIC_AUTH_RESOURCE,
  ].filter(Boolean) as string[],
  trackSessionAcrossSubdomains: true,
  beforeSend: (event: any) => {
    if (
      event.type === 'error' &&
      ddIgnorableErrors.some((error: string) =>
        event.error.message.includes(error)
      )
    ) {
      return false;
    }
    return true;
  },
};

type CaptureExceptionAdditionalData = {
  req?: {
    url?: string;
    method?: string;
    headers?: unknown;
    params?: unknown;
    query?: unknown;
  };
  res?: { statusCode?: number };
  errorInfo?: { componentStack?: string } & Record<string, unknown>;
  level?: string;
  query?: unknown;
  pathname?: unknown;
  tags?: Record<string, string>;
  statusCode?: number;
};

const captureException = (
  err:
    | (Error & { statusCode?: number })
    | { message?: string; statusCode?: number },
  additionalData?: CaptureExceptionAdditionalData
): void => {
  if (!(err instanceof Error)) {
    return;
  }

  const wrapped = new Error(err.message || 'Unknown error');
  wrapped.stack =
    additionalData?.errorInfo?.componentStack ||
    err.stack ||
    'No stack trace available';
  (wrapped as Error & { cause?: unknown }).cause = err;

  const context: Record<string, unknown> = {};
  const statusCode =
    (err as { statusCode?: number }).statusCode ??
    additionalData?.res?.statusCode;
  if (statusCode != null) context.statusCode = statusCode;
  if (additionalData?.level) context.level = additionalData.level;
  if (additionalData?.query !== undefined) context.query = additionalData.query;
  if (additionalData?.pathname !== undefined)
    context.pathname = additionalData.pathname;
  if (additionalData?.tags) context.tags = additionalData.tags;
  if (additionalData?.errorInfo) {
    Object.entries(additionalData.errorInfo).forEach(([key, value]) => {
      if (key !== 'componentStack') context[key] = value;
    });
  }
  if (typeof window === 'undefined' && additionalData?.req) {
    context.ssr = true;
    context.url = additionalData.req.url;
    context.method = additionalData.req.method;
    context.headers = additionalData.req.headers;
    context.params = additionalData.req.params;
    context.requestQuery = additionalData.req.query;
  } else {
    context.ssr = false;
  }

  datadogRum.addError(wrapped, context);
};

datadogRum.init(datadogOptions);

export { captureException };
